import { IMockupGenerationLog, asBool } from '../../api/mockupGenerationLogs';

export interface ICount {
    label: string;
    count: number;
}

export interface ITimeBucket {
    key: string;
    label: string;
    total: number;
    ok: number;
    error: number;
    avgSec: number | null;
    p50Sec: number | null;
    p95Sec: number | null;
}

export interface IStage {
    key: string;
    label: string;
    avgSec: number;
}

export interface IFlagShare {
    key: string;
    label: string;
    trueCount: number;
    known: number;
    share: number;
}

export interface IHistogramBucket {
    label: string;
    count: number;
}

export interface IKpis {
    total: number;
    ok: number;
    error: number;
    errorRate: number;
    avgTotalSec: number | null;
    p50TotalSec: number | null;
    p95TotalSec: number | null;
    maxTotalSec: number | null;
    avgRbSec: number | null;
    avgP3dSec: number | null;
    avgGlbBytes: number | null;
    avgTriangles: number | null;
    avgQuality: number | null;
    avgConfidence: number | null;
    avgEdgeQuality: number | null;
    avgContentRetention: number | null;
    avgSourceMp: number | null;
    lostSubjectCount: number;
    multiComponentCount: number;
    silComponentsKnown: number;
    retryCount: number;
    noBgFastPathCount: number;
    texFromSourceShare: number | null;
    overridesUsedCount: number;
}

export interface IGenerationLogsAggregate {
    kpis: IKpis;
    byStatus: ICount[];
    byErrorKind: ICount[];
    byHttpStatus: ICount[];
    byModelType: ICount[];
    byDevice: ICount[];
    byResponseMode: ICount[];
    byServerHost: ICount[];
    byServerVersion: ICount[];
    byTemplate: ICount[];
    byTargetResolution: ICount[];
    timeline: ITimeBucket[];
    timelineUnit: 'hour' | 'day';
    stages: IStage[];
    totalSecHistogram: IHistogramBucket[];
    qualityHistogram: IHistogramBucket[];
    silComponentsHistogram: IHistogramBucket[];
    flags: IFlagShare[];
    scatterMpVsSec: { x: number; y: number; ok: boolean }[];
    scatterQualityVsRetention: { x: number; y: number }[];
    errorMessages: { message: string; kind: string; count: number }[];
}

const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
};

const mean = (values: number[]): number | null =>
    values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;

export const percentile = (values: number[], p: number): number | null => {
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
    return sorted[idx];
};

const countBy = (rows: IMockupGenerationLog[], pick: (r: IMockupGenerationLog) => unknown): ICount[] => {
    const map = new Map<string, number>();
    rows.forEach((r) => {
        const v = pick(r);
        const label = v === null || v === undefined || v === '' ? 'unknown' : String(v);
        map.set(label, (map.get(label) || 0) + 1);
    });
    return Array.from(map.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);
};

const pad2 = (n: number) => String(n).padStart(2, '0');

const bucketKey = (iso: string, unit: 'hour' | 'day'): { key: string; label: string } | null => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getUTCFullYear();
    const m = pad2(d.getUTCMonth() + 1);
    const day = pad2(d.getUTCDate());
    if (unit === 'day') return { key: `${y}-${m}-${day}`, label: `${m}-${day}` };
    const h = pad2(d.getUTCHours());
    return { key: `${y}-${m}-${day}T${h}`, label: `${m}-${day} ${h}:00` };
};

const enumerateBuckets = (minIso: string, maxIso: string, unit: 'hour' | 'day'): { key: string; label: string }[] => {
    const out: { key: string; label: string }[] = [];
    const start = new Date(minIso);
    const end = new Date(maxIso);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return out;
    const cur = new Date(Date.UTC(
        start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(),
        unit === 'hour' ? start.getUTCHours() : 0
    ));
    let guard = 0;
    while (cur.getTime() <= end.getTime() && guard < 5000) {
        const b = bucketKey(cur.toISOString(), unit);
        if (b) out.push(b);
        if (unit === 'hour') cur.setUTCHours(cur.getUTCHours() + 1);
        else cur.setUTCDate(cur.getUTCDate() + 1);
        guard += 1;
    }
    return out;
};

const histogram = (values: number[], edges: number[], fmt: (a: number, b: number | null) => string): IHistogramBucket[] => {
    const buckets: IHistogramBucket[] = [];
    for (let i = 0; i < edges.length; i++) {
        const lo = edges[i];
        const hi = i + 1 < edges.length ? edges[i + 1] : null;
        const count = values.filter((v) => v >= lo && (hi === null || v < hi)).length;
        buckets.push({ label: fmt(lo, hi), count });
    }
    return buckets;
};

const flagDefs: { key: keyof IMockupGenerationLog; label: string }[] = [
    { key: 'glbOnly', label: 'glbOnly' },
    { key: 'neutralViewer', label: 'neutralViewer' },
    { key: 'sourceProvided', label: 'sourceProvided' },
    { key: 'noBgProvided', label: 'noBgProvided (fast path)' },
    { key: 'rbSkipped', label: 'rbSkipped' },
    { key: 'rbPreview', label: 'rbPreview' },
    { key: 'fastMode', label: 'fastMode' },
    { key: 'balancedMode', label: 'balancedMode' },
    { key: 'includePreviews', label: 'includePreviews' },
    { key: 'overridesApplied', label: 'overridesApplied' },
    { key: 'rbFbaApplied', label: 'rbFbaApplied' },
    { key: 'rbRetryPerformed', label: 'rbRetryPerformed' },
    { key: 'texFromSource', label: 'texFromSource' },
    { key: 'orientationFixed', label: 'orientationFixed' },
    { key: 'blackBordersCropped', label: 'blackBordersCropped' },
    { key: 'p3dTagBuilt', label: 'p3dTagBuilt' },
];

export const LOST_SUBJECT_THRESHOLD = 0.15;

export function aggregateGenerationLogs(rows: IMockupGenerationLog[]): IGenerationLogsAggregate {
    const okRows = rows.filter((r) => r.status === 'ok');
    const errRows = rows.filter((r) => r.status !== 'ok');

    const totalSecs = rows.map((r) => num(r.totalSec)).filter((v): v is number => v !== null);
    const rbSecs = okRows.map((r) => num(r.rbSec)).filter((v): v is number => v !== null);
    const p3dSecs = okRows
        .map((r) => {
            const parts = [num(r.p3dMeshSec), num(r.p3dGlbSec), num(r.p3dPreviewsSec)].filter((v): v is number => v !== null);
            return parts.length ? parts.reduce((a, b) => a + b, 0) : null;
        })
        .filter((v): v is number => v !== null);

    const pick = (k: keyof IMockupGenerationLog, src: IMockupGenerationLog[] = okRows) =>
        src.map((r) => num(r[k])).filter((v): v is number => v !== null);

    const qualities = pick('rbOverallQuality');
    const silIgnored = pick('p3dSilIgnoredShare');
    const silComponents = pick('p3dSilComponents');

    const texKnown = rows.map((r) => asBool(r.texFromSource)).filter((v): v is boolean => v !== null);

    const kpis: IKpis = {
        total: rows.length,
        ok: okRows.length,
        error: errRows.length,
        errorRate: rows.length ? errRows.length / rows.length : 0,
        avgTotalSec: mean(totalSecs),
        p50TotalSec: percentile(totalSecs, 50),
        p95TotalSec: percentile(totalSecs, 95),
        maxTotalSec: totalSecs.length ? Math.max(...totalSecs) : null,
        avgRbSec: mean(rbSecs),
        avgP3dSec: mean(p3dSecs),
        avgGlbBytes: mean(pick('p3dGlbBytes')),
        avgTriangles: mean(pick('p3dTriangles')),
        avgQuality: mean(qualities),
        avgConfidence: mean(pick('rbConfidence')),
        avgEdgeQuality: mean(pick('rbEdgeQuality')),
        avgContentRetention: mean(pick('rbContentRetention')),
        avgSourceMp: mean(pick('sourceMp', rows)),
        lostSubjectCount: silIgnored.filter((v) => v > LOST_SUBJECT_THRESHOLD).length,
        multiComponentCount: silComponents.filter((v) => v > 1).length,
        silComponentsKnown: silComponents.length,
        retryCount: rows.filter((r) => asBool(r.rbRetryPerformed) === true).length,
        noBgFastPathCount: rows.filter((r) => asBool(r.noBgProvided) === true).length,
        texFromSourceShare: texKnown.length ? texKnown.filter(Boolean).length / texKnown.length : null,
        overridesUsedCount: rows.filter((r) => (num(r.overridesCount) || 0) > 0).length,
    };

    // ---- timeline ----
    const tsValues = rows.map((r) => r.ts).filter(Boolean).sort();
    const minTs = tsValues[0];
    const maxTs = tsValues[tsValues.length - 1];
    let timelineUnit: 'hour' | 'day' = 'day';
    if (minTs && maxTs) {
        const spanH = (new Date(maxTs).getTime() - new Date(minTs).getTime()) / 36e5;
        timelineUnit = spanH <= 48 ? 'hour' : 'day';
    }
    const tlMap = new Map<string, { label: string; ok: number; error: number; secs: number[] }>();
    if (minTs && maxTs) {
        enumerateBuckets(minTs, maxTs, timelineUnit).forEach((b) =>
            tlMap.set(b.key, { label: b.label, ok: 0, error: 0, secs: [] })
        );
    }
    rows.forEach((r) => {
        const b = bucketKey(r.ts, timelineUnit);
        if (!b) return;
        if (!tlMap.has(b.key)) tlMap.set(b.key, { label: b.label, ok: 0, error: 0, secs: [] });
        const e = tlMap.get(b.key)!;
        if (r.status === 'ok') e.ok += 1;
        else e.error += 1;
        const s = num(r.totalSec);
        if (s !== null) e.secs.push(s);
    });
    const timeline: ITimeBucket[] = Array.from(tlMap.entries())
        .sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([key, e]) => ({
            key,
            label: e.label,
            total: e.ok + e.error,
            ok: e.ok,
            error: e.error,
            avgSec: mean(e.secs),
            p50Sec: percentile(e.secs, 50),
            p95Sec: percentile(e.secs, 95),
        }));

    // ---- pipeline stages (average over ok rows with totalSec) ----
    const stageRows = okRows.filter((r) => num(r.totalSec) !== null);
    const avgOf = (fn: (r: IMockupGenerationLog) => number | null) =>
        mean(stageRows.map(fn).filter((v): v is number => v !== null)) || 0;
    const rbInf = avgOf((r) => (num(r.rbInferenceMs) ?? 0) / 1000);
    const rbFba = avgOf((r) => (num(r.rbFbaMs) ?? 0) / 1000);
    const rbPost = avgOf((r) => (num(r.rbPostprocessMs) ?? 0) / 1000);
    const rbOther = avgOf((r) => {
        const total = num(r.rbSec);
        if (total === null) return null;
        const known = ((num(r.rbInferenceMs) ?? 0) + (num(r.rbFbaMs) ?? 0) + (num(r.rbPostprocessMs) ?? 0)) / 1000;
        return Math.max(0, total - known);
    });
    const mesh = avgOf((r) => num(r.p3dMeshSec) ?? 0);
    const glb = avgOf((r) => num(r.p3dGlbSec) ?? 0);
    const previews = avgOf((r) => num(r.p3dPreviewsSec) ?? 0);
    const other = avgOf((r) => {
        const total = num(r.totalSec) ?? 0;
        const known = (num(r.rbSec) ?? 0) + (num(r.p3dMeshSec) ?? 0) + (num(r.p3dGlbSec) ?? 0) + (num(r.p3dPreviewsSec) ?? 0);
        return Math.max(0, total - known);
    });
    const stages: IStage[] = [
        { key: 'rbInference', label: 'RB inference', avgSec: rbInf },
        { key: 'rbFba', label: 'RB FBA matting', avgSec: rbFba },
        { key: 'rbPostprocess', label: 'RB postprocess', avgSec: rbPost },
        { key: 'rbOther', label: 'RB other (pre/IO)', avgSec: rbOther },
        { key: 'p3dMesh', label: '3D mesh', avgSec: mesh },
        { key: 'p3dGlb', label: '3D GLB export', avgSec: glb },
        { key: 'p3dPreviews', label: '3D previews', avgSec: previews },
        { key: 'other', label: 'Other (contour, IO, response)', avgSec: other },
    ];

    // ---- histograms ----
    const totalSecHistogram = histogram(
        totalSecs,
        [0, 0.5, 1, 1.5, 2, 3, 5, 10],
        (lo, hi) => (hi === null ? `${lo}s+` : `${lo}–${hi}s`)
    );
    const qualityHistogram = histogram(
        qualities,
        [0, 0.2, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9],
        (lo, hi) => (hi === null ? `${lo}–1.0` : `${lo}–${hi}`)
    );
    const silComponentsHistogram = histogram(
        silComponents,
        [1, 2, 3, 4, 6, 11],
        (lo, hi) => (hi === null ? `${lo}+` : hi - lo === 1 ? `${lo}` : `${lo}–${hi - 1}`)
    );

    // ---- flags ----
    const flags: IFlagShare[] = flagDefs.map(({ key, label }) => {
        const known = rows.map((r) => asBool(r[key])).filter((v): v is boolean => v !== null);
        const trueCount = known.filter(Boolean).length;
        return { key: String(key), label, trueCount, known: known.length, share: known.length ? trueCount / known.length : 0 };
    });

    // ---- scatter ----
    const scatterMpVsSec = rows
        .map((r) => ({ x: num(r.sourceMp), y: num(r.totalSec), ok: r.status === 'ok' }))
        .filter((p): p is { x: number; y: number; ok: boolean } => p.x !== null && p.y !== null);
    const scatterQualityVsRetention = okRows
        .map((r) => ({ x: num(r.rbContentRetention), y: num(r.rbOverallQuality) }))
        .filter((p): p is { x: number; y: number } => p.x !== null && p.y !== null);

    // ---- error messages ----
    const errMap = new Map<string, { message: string; kind: string; count: number }>();
    errRows.forEach((r) => {
        const message = (r.errorMessage || r.errorJson?.message || 'unknown').slice(0, 140);
        const kind = r.errorKind || r.errorJson?.kind || 'unknown';
        const k = `${kind}|${message}`;
        if (!errMap.has(k)) errMap.set(k, { message, kind, count: 0 });
        errMap.get(k)!.count += 1;
    });

    return {
        kpis,
        byStatus: countBy(rows, (r) => r.status),
        byErrorKind: countBy(errRows, (r) => r.errorKind || r.errorJson?.kind),
        byHttpStatus: countBy(rows, (r) => r.httpStatus),
        byModelType: countBy(rows, (r) => r.modelType),
        byDevice: countBy(rows, (r) => r.rbDevice),
        byResponseMode: countBy(rows, (r) => r.responseMode),
        byServerHost: countBy(rows, (r) => r.serverHost),
        byServerVersion: countBy(rows, (r) => r.serverVersion),
        byTemplate: countBy(rows, (r) => r.template),
        byTargetResolution: countBy(rows, (r) => r.targetResolution),
        timeline,
        timelineUnit,
        stages,
        totalSecHistogram,
        qualityHistogram,
        silComponentsHistogram,
        flags,
        scatterMpVsSec,
        scatterQualityVsRetention,
        errorMessages: Array.from(errMap.values()).sort((a, b) => b.count - a.count),
    };
}
